import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = 1024 * 1024;

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "-";
  if (bytes < BYTES_PER_KB) return `${bytes} B`;
  if (bytes < BYTES_PER_MB) return `${Math.round(bytes / BYTES_PER_KB)} KB`;
  return `${(bytes / BYTES_PER_MB).toFixed(1)} MB`;
};

const FileTable = ({
  files = [],
  onOpenMapping,
  onRename,
  onMove,
  onToggleActive,
  onDelete,
  onSend,
  canEdit = false,
  canDelete = false,
  canSend = false,
}) => {
  const [menu, setMenu] = useState({ anchor: null, file: null });
  const closeMenu = () => setMenu({ anchor: null, file: null });

  if (files.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No documents in this folder yet.
      </Typography>
    );
  }

  return (
    <Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Pages</TableCell>
            <TableCell>Size</TableCell>
            <TableCell>Mapping</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {files.map((file) => (
            <TableRow key={file.id} hover>
              <TableCell>
                <Typography variant="body2">{file.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {file.originalFilename}
                </Typography>
                {file.active === false && <Chip size="small" label="Inactive" sx={{ ml: 1 }} />}
              </TableCell>
              <TableCell>{file.pageCount ?? "-"}</TableCell>
              <TableCell>{formatSize(file.fileSizeBytes)}</TableCell>
              <TableCell>
                {file.mappedFieldCount > 0 ? (
                  <Chip size="small" color="success" label={`${file.mappedFieldCount} fields`} />
                ) : (
                  <Chip size="small" label="Not mapped" />
                )}
              </TableCell>
              <TableCell align="right">
                {canSend && (
                  <Button
                    size="small"
                    disabled={!file.mappedFieldCount || file.active === false}
                    onClick={() => onSend(file)}
                  >
                    Send
                  </Button>
                )}
                {canEdit && (
                  <Button size="small" onClick={() => onOpenMapping(file)}>
                    Map fields
                  </Button>
                )}
                {(canEdit || canDelete) && (
                  <IconButton
                    size="small"
                    onClick={(event) => setMenu({ anchor: event.currentTarget, file })}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Menu anchorEl={menu.anchor} open={Boolean(menu.anchor)} onClose={closeMenu}>
        {canEdit && (
          <MenuItem
            onClick={() => {
              onRename(menu.file);
              closeMenu();
            }}
          >
            Rename
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem
            onClick={() => {
              onMove(menu.file);
              closeMenu();
            }}
          >
            Move to folder
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem
            onClick={() => {
              onToggleActive(menu.file);
              closeMenu();
            }}
          >
            {menu.file?.active === false ? "Reactivate" : "Deactivate"}
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            onClick={() => {
              onDelete(menu.file);
              closeMenu();
            }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default FileTable;
